import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Search, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GSCKeywordsWidget = ({ token, configId, userEmail, startDate, endDate }) => {
  const [loading, setLoading] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchGSCData = async () => {
    if (!token || !configId || !startDate || !endDate) return;
    
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch top keywords and summary in parallel
      const [keywordsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/gsc/top-keywords`, {
          params: { start_date: startDate, end_date: endDate, config_id: configId, limit: 20 },
          headers
        }),
        axios.get(`${API}/gsc/summary`, {
          params: { start_date: startDate, end_date: endDate, config_id: configId },
          headers
        })
      ]);
      
      setKeywords(keywordsRes.data.keywords || []);
      setSummary(summaryRes.data.summary || null);
    } catch (error) {
      console.error('GSC fetch error:', error);
      // Don't show error toast if it's a permission issue - GSC may not be set up
      if (error.response?.status !== 500 || !error.response?.data?.detail?.includes('Permission')) {
        // Silent fail - GSC might not be configured
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGSCData();
  }, [token, configId, startDate, endDate]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  const getPositionColor = (position) => {
    if (position <= 3) return 'text-green-600';
    if (position <= 10) return 'text-yellow-600';
    if (position <= 20) return 'text-orange-600';
    return 'text-red-600';
  };

  const displayedKeywords = expanded ? keywords : keywords.slice(0, 5);

  return (
    <Card className="bg-white border border-slate-200 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Search className="w-5 h-5 text-blue-500" />
          <CardTitle className="text-lg text-slate-800">Top Ranking Keywords</CardTitle>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchGSCData}
          disabled={loading}
          className="text-slate-600 hover:text-slate-800"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{formatNumber(summary.total_clicks)}</p>
              <p className="text-xs text-slate-500">Clicks</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-purple-600">{formatNumber(summary.total_impressions)}</p>
              <p className="text-xs text-slate-500">Impressions</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{summary.average_ctr}%</p>
              <p className="text-xs text-slate-500">Avg CTR</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{summary.average_position}</p>
              <p className="text-xs text-slate-500">Avg Position</p>
            </div>
          </div>
        )}

        {/* Keywords Table */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : keywords.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Search className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No keyword data available</p>
            <p className="text-xs mt-1">Make sure Search Console is connected</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2 font-medium">Keyword</th>
                    <th className="text-right py-2 font-medium">Position</th>
                    <th className="text-right py-2 font-medium">Clicks</th>
                    <th className="text-right py-2 font-medium">Impr.</th>
                    <th className="text-right py-2 font-medium">CTR</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedKeywords.map((kw, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 pr-4 text-slate-700">
                        <span className="line-clamp-1" title={kw.query}>
                          {kw.query}
                        </span>
                      </td>
                      <td className={`text-right py-2 font-semibold ${getPositionColor(kw.position)}`}>
                        {kw.position}
                      </td>
                      <td className="text-right py-2 text-blue-600">{formatNumber(kw.clicks)}</td>
                      <td className="text-right py-2 text-slate-500">{formatNumber(kw.impressions)}</td>
                      <td className="text-right py-2 text-green-600">{kw.ctr}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {keywords.length > 5 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="w-full mt-3 text-slate-500 hover:text-slate-700"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" /> Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" /> Show {keywords.length - 5} More
                  </>
                )}
              </Button>
            )}
          </>
        )}

        {/* Info text */}
        <p className="text-xs text-slate-500 mt-3 text-center">
          Data from Google Search Console • Rankings may take 2-3 days to update
        </p>
      </CardContent>
    </Card>
  );
};

export default GSCKeywordsWidget;
